'use client';

import { c as _c } from "react/compiler-runtime";
import classNames from 'classnames';
import { useForm } from 'react-hook-form';
import { useAccountSettingsPageContext } from '@module/mdrt-org/account-settings/providers/account-settings-page-context';
import { PushNotificationPreferenceToggle } from '@module/mdrt-org/shared/components/notification-preference-toggle/push-notification-preference-toggle';
import { useUserContext } from '@module/mdrt-org/shared/components/providers/user-context';
import { SelectField } from '@module/mdrt-org/shared/components/select-field/select-field';
import { type InterestsData } from '@module/mdrt-org/shared/utils/data/graphql/utils/get-topics';
import { patchAccountSettingsAction } from '@module/mdrt-org/shared/utils/data/patch-account-settings-action';
import { AccountSettingsFormStep } from '@module/mdrt-org/shared/utils/enums/account-settings-form-step';
import { AccountSettingsMutationKey } from '@module/mdrt-org/shared/utils/enums/account-settings-mutation-key';
import { splitByCharacterLimit } from '@module/mdrt-org/shared/utils/helpers/split-by-character-limit';
import { useAddDefaultErrorToast } from '@module/mdrt-org/shared/utils/hooks/use-add-default-error-toast';
import { useConfirmNavigation } from '@module/mdrt-org/shared/utils/hooks/use-confirm-navigation';
import { useMutationHandler } from '@module/mdrt-org/shared/utils/hooks/use-mutation-handler';
import { type CenterCommunicationTopics, type CommunicationTopics } from '@module/mdrt-org/shared/utils/types/customer-profile';
import { Button } from '@shared/ui-components/button/button';
import { CheckboxField } from '@shared/ui-components/checkbox-field/checkbox-field';
import { FormWrapper } from '@shared/ui-components/form-wrapper/form-wrapper';
import { formBaseConfig } from '@shared/utils/constants/formBaseConfig';
import { GTM_ACTION_CLICK, GTM_EVENT_EMAILSMS_PREFERENCE_CLICK, GTM_EVENT_TOPIC_INTEREST_CLICK, GTM_EVENT_USER_INTERACTIONS } from '@shared/utils/constants/gtm';
import { ButtonType } from '@shared/utils/enums/button-type';
import { sendGtmEvent } from '@shared/utils/helpers/send-gtm-event';
import { type SelectOption } from '@shared/utils/types/select-option';
import { type ServerActionResponse } from '@shared/utils/types/server-action';
import { type SitePreferencesData, type SitePreferencesFormData } from '../../utils/types/form-data';
import { type AccountSettingsSitePreferencesContent } from './get-account-settings-site-preferences-content';
import { InterestsSection } from './interests-section/interests-section';
import { CommunicationPreferenceCenterTypes } from './utils/get-current-preference-center';
import styles from './site-preferences.module.scss';
type SitePreferencesFormProps = {
  cmsContent: AccountSettingsSitePreferencesContent;
  languagesOptions: SelectOption[];
  tabUrl: string;
  topics: InterestsData;
};
type CommunicationTopicKeys = keyof CommunicationTopics;
type CenterCommunicationTopicsKeys = keyof CenterCommunicationTopics;
export const SitePreferencesForm = t0 => {
  const $ = _c(131);
  const {
    cmsContent,
    languagesOptions,
    tabUrl,
    topics
  } = t0;
  const {
    communicationPreferencesContextData,
    customerProfileContextData,
    resetCommunicationPreferencesContextData,
    resetCustomerProfileContextData,
    setCommunicationPreferencesContextData,
    setCustomerProfileContextData
  } = useAccountSettingsPageContext();
  const {
    personId
  } = useUserContext();
  const addErrorToast = useAddDefaultErrorToast();
  let t1;
  if ($[0] !== cmsContent.addMoreTopicsButtonLabel || $[1] !== cmsContent.cancelButtonLabel || $[2] !== cmsContent.interestModalSaveButtonLabel || $[3] !== cmsContent.interestModalSubTitle || $[4] !== cmsContent.interestModalTitle || $[5] !== cmsContent.interestsErrorMessage || $[6] !== cmsContent.interestsTitle || $[7] !== cmsContent.selectTopics) {
    t1 = {
      addMoreTopicsButtonLabel: cmsContent.addMoreTopicsButtonLabel,
      cancelButtonLabel: cmsContent.cancelButtonLabel,
      description: cmsContent.selectTopics,
      interestModalSubTitle: cmsContent.interestModalSubTitle,
      interestModalTitle: cmsContent.interestModalTitle,
      interestsErrorMessage: cmsContent.interestsErrorMessage,
      saveButtonLabel: cmsContent.interestModalSaveButtonLabel,
      title: cmsContent.interestsTitle
    };
    $[0] = cmsContent.addMoreTopicsButtonLabel;
    $[1] = cmsContent.cancelButtonLabel;
    $[2] = cmsContent.interestModalSaveButtonLabel;
    $[3] = cmsContent.interestModalSubTitle;
    $[4] = cmsContent.interestModalTitle;
    $[5] = cmsContent.interestsErrorMessage;
    $[6] = cmsContent.interestsTitle;
    $[7] = cmsContent.selectTopics;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  const interestsSectionCmsContent = t1;
  let t2;
  if ($[9] !== customerProfileContextData.interests || $[10] !== topics) {
    t2 = customerProfileContextData.interests ? topics.filter(topic => customerProfileContextData.interests.includes(topic.topicId)) : [];
    $[9] = customerProfileContextData.interests;
    $[10] = topics;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  let t3;
  if ($[12] !== customerProfileContextData.primaryLanguage || $[13] !== languagesOptions) {
    t3 = customerProfileContextData.primaryLanguage ? {
      label: languagesOptions.find(language => language?.value === customerProfileContextData.primaryLanguage)?.label,
      value: customerProfileContextData.primaryLanguage
    } : null;
    $[12] = customerProfileContextData.primaryLanguage;
    $[13] = languagesOptions;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  let t4;
  if ($[15] !== communicationPreferencesContextData.centerCommunicationTopics || $[16] !== communicationPreferencesContextData.communicationCenter || $[17] !== communicationPreferencesContextData.communicationTopics || $[18] !== communicationPreferencesContextData.contactPermission || $[19] !== customerProfileContextData.memberDirectoryOptIn || $[20] !== t2 || $[21] !== t3) {
    t4 = {
      ...formBaseConfig,
      defaultValues: {
        centerCommunicationTopics: communicationPreferencesContextData.centerCommunicationTopics,
        communicationCenter: communicationPreferencesContextData.communicationCenter,
        communicationTopics: communicationPreferencesContextData.communicationTopics,
        contactPermission: communicationPreferencesContextData.contactPermission,
        interests: t2,
        memberDirectoryOptIn: customerProfileContextData.memberDirectoryOptIn,
        preferredLanguage: t3
      }
    };
    $[15] = communicationPreferencesContextData.centerCommunicationTopics;
    $[16] = communicationPreferencesContextData.communicationCenter;
    $[17] = communicationPreferencesContextData.communicationTopics;
    $[18] = communicationPreferencesContextData.contactPermission;
    $[19] = customerProfileContextData.memberDirectoryOptIn;
    $[20] = t2;
    $[21] = t3;
    $[22] = t4;
  } else {
    t4 = $[22];
  }
  const formMethods = useForm(t4);
  const {
    formState: t5,
    reset
  } = formMethods;
  const {
    isDirty
  } = t5;
  let t6;
  if ($[23] !== addErrorToast || $[24] !== resetCommunicationPreferencesContextData || $[25] !== resetCustomerProfileContextData) {
    t6 = result => {
      if (result.status !== 200 && "message" in result) {
        resetCustomerProfileContextData();
        resetCommunicationPreferencesContextData();
        addErrorToast();
      }
    };
    $[23] = addErrorToast;
    $[24] = resetCommunicationPreferencesContextData;
    $[25] = resetCustomerProfileContextData;
    $[26] = t6;
  } else {
    t6 = $[26];
  }
  const onFailure = t6;
  let t7;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = [AccountSettingsMutationKey.PATCH_ACCOUNT_SETTINGS];
    $[27] = t7;
  } else {
    t7 = $[27];
  }
  let t8;
  if ($[28] !== formMethods || $[29] !== reset) {
    t8 = () => reset({
      ...formMethods.getValues()
    });
    $[28] = formMethods;
    $[29] = reset;
    $[30] = t8;
  } else {
    t8 = $[30];
  }
  let t9;
  if ($[31] !== onFailure || $[32] !== t8) {
    t9 = {
      mutationFunction: patchAccountSettingsAction,
      mutationKey: t7,
      onFailure,
      onSuccessHandler: t8
    };
    $[31] = onFailure;
    $[32] = t8;
    $[33] = t9;
  } else {
    t9 = $[33];
  }
  const {
    isMutationPending,
    mutate
  } = useMutationHandler(t9);
  let t10;
  if ($[34] !== cmsContent.MDRTFoundationLabel || $[35] !== cmsContent.MDRTMeetingsLabel || $[36] !== cmsContent.MDRTStoreLabel || $[37] !== cmsContent.businessAnnouncementsLabel || $[38] !== cmsContent.contentNewsletterLabel || $[39] !== cmsContent.memberBenefitsLabel || $[40] !== communicationPreferencesContextData.communicationCenter) {
    t10 = communicationPreferencesContextData.communicationCenter === CommunicationPreferenceCenterTypes.MDRT ? [{
      label: cmsContent.memberBenefitsLabel,
      name: "communicationTopics.memberBenefits"
    }, {
      label: cmsContent.contentNewsletterLabel,
      name: "communicationTopics.contentNewsletter"
    }, {
      label: cmsContent.MDRTMeetingsLabel,
      name: "communicationTopics.mdrtMeetings"
    }, {
      label: cmsContent.businessAnnouncementsLabel,
      name: "communicationTopics.businessAnnouncements"
    }, {
      label: cmsContent.MDRTFoundationLabel,
      name: "communicationTopics.mdrtFoundation"
    }, {
      label: cmsContent.MDRTStoreLabel,
      name: "communicationTopics.mdrtStore"
    }] : communicationPreferencesContextData.communicationCenter === CommunicationPreferenceCenterTypes.Center ? [{
      label: cmsContent.memberBenefitsLabel,
      name: "centerCommunicationTopics.memberBenefits"
    }, {
      label: cmsContent.contentNewsletterLabel,
      name: "centerCommunicationTopics.contentNewsletter"
    }, {
      label: cmsContent.MDRTMeetingsLabel,
      name: "centerCommunicationTopics.mdrtMeetings"
    }, {
      label: cmsContent.businessAnnouncementsLabel,
      name: "centerCommunicationTopics.businessAnnouncements"
    }] : [];
    $[34] = cmsContent.MDRTFoundationLabel;
    $[35] = cmsContent.MDRTMeetingsLabel;
    $[36] = cmsContent.MDRTStoreLabel;
    $[37] = cmsContent.businessAnnouncementsLabel;
    $[38] = cmsContent.contentNewsletterLabel;
    $[39] = cmsContent.memberBenefitsLabel;
    $[40] = communicationPreferencesContextData.communicationCenter;
    $[41] = t10;
  } else {
    t10 = $[41];
  }
  const emailNotifications = t10;
  const hasDifferentNames = _temp3;
  const hasMismatch = _temp4;
  let t11;
  if ($[42] !== communicationPreferencesContextData.centerCommunicationTopics || $[43] !== communicationPreferencesContextData.communicationCenter || $[44] !== communicationPreferencesContextData.communicationTopics || $[45] !== communicationPreferencesContextData.contactPermission.smsPermission || $[46] !== customerProfileContextData || $[47] !== mutate || $[48] !== personId || $[49] !== setCommunicationPreferencesContextData || $[50] !== setCustomerProfileContextData || $[51] !== topics) {
    t11 = async data => {
      setCustomerProfileContextData({
        ...customerProfileContextData,
        interests: data.interests && data.interests.map(_temp5),
        memberDirectoryOptIn: data.memberDirectoryOptIn,
        primaryLanguage: data.preferredLanguage && Number(data.preferredLanguage.value)
      });
      setCommunicationPreferencesContextData({
        centerCommunicationTopics: communicationPreferencesContextData.communicationCenter === CommunicationPreferenceCenterTypes.Center ? data.communicationTopics : null,
        communicationCenter: data.communicationCenter,
        communicationTopics: communicationPreferencesContextData.communicationCenter === CommunicationPreferenceCenterTypes.MDRT ? data.communicationTopics : null,
        contactPermission: data.contactPermission
      });
      mutate({
        formData: {
          accountSettingStep: AccountSettingsFormStep.SitePreferences,
          permissionData: {
            centerCommunicationTopics: communicationPreferencesContextData.communicationCenter === CommunicationPreferenceCenterTypes.Center ? data.centerCommunicationTopics : null,
            communicationCenter: data.communicationCenter,
            communicationTopics: communicationPreferencesContextData.communicationCenter === CommunicationPreferenceCenterTypes.MDRT ? data.communicationTopics : null,
            contactPermission: data.contactPermission
          },
          sitePreferenceData: {
            interests: data.interests && data.interests.map(_temp6),
            memberDirectoryOptIn: data.memberDirectoryOptIn,
            preferredLanguageId: data.preferredLanguage && Number(data.preferredLanguage.value)
          }
        },
        personId
      });
      const savedInterests = customerProfileContextData.interests ? topics.filter(topic_0 => customerProfileContextData.interests.includes(topic_0.topicId)) : [];
      const isDirtySavedInterests = hasDifferentNames(savedInterests, data?.interests);
      let isDirtyEmailPrefernce = false;
      if (communicationPreferencesContextData.communicationCenter === "mdrtPreferenceCenter") {
        isDirtyEmailPrefernce = hasMismatch(communicationPreferencesContextData.communicationTopics, data.communicationTopics);
      } else {
        if (communicationPreferencesContextData.communicationCenter === "mdrtCenterPreferenceCenter") {
          isDirtyEmailPrefernce = hasMismatch(communicationPreferencesContextData.centerCommunicationTopics, data.centerCommunicationTopics);
        }
      }
      if (communicationPreferencesContextData.contactPermission.smsPermission !== data?.contactPermission?.smsPermission) {
        isDirtyEmailPrefernce = true;
      }
      if (customerProfileContextData.memberDirectoryOptIn !== data?.memberDirectoryOptIn) {
        isDirtyEmailPrefernce = true;
      }
      if (isDirtyEmailPrefernce) {
        let currentCommunicationTopics = data.centerCommunicationTopics;
        if (communicationPreferencesContextData.communicationCenter === "mdrtPreferenceCenter") {
          currentCommunicationTopics = data.communicationTopics;
        }
        let selectedCommunicationTopics = Object.entries(currentCommunicationTopics ?? {}).filter(_temp7).map(_temp8).join(", ");
        const additionalTopics = [];
        if (data?.contactPermission?.smsPermission) {
          additionalTopics.push("sms_notifications");
        }
        if (data?.memberDirectoryOptIn) {
          additionalTopics.push("member_directory");
        }
        if (additionalTopics.length > 0) {
          selectedCommunicationTopics = selectedCommunicationTopics ? `${selectedCommunicationTopics}, ${additionalTopics.join(", ")}` : additionalTopics.join(", ");
        }
        if (selectedCommunicationTopics?.length > 0) {
          const topicChunks = splitByCharacterLimit(selectedCommunicationTopics, 100);
          for (const chunk of topicChunks) {
            sendGtmEvent({
              action: GTM_ACTION_CLICK,
              event: GTM_EVENT_USER_INTERACTIONS,
              eventName: GTM_EVENT_EMAILSMS_PREFERENCE_CLICK,
              label: chunk
            });
          }
        }
      }
      if (isDirtySavedInterests) {
        const selctedTopicInterests = data?.interests?.map(_temp9).join(", ");
        const topicChunks_0 = splitByCharacterLimit(selctedTopicInterests, 100);
        for (const chunk_0 of topicChunks_0) {
          sendGtmEvent({
            action: GTM_ACTION_CLICK,
            event: GTM_EVENT_USER_INTERACTIONS,
            eventName: GTM_EVENT_TOPIC_INTEREST_CLICK,
            label: chunk_0
          });
        }
      }
    };
    $[42] = communicationPreferencesContextData.centerCommunicationTopics;
    $[43] = communicationPreferencesContextData.communicationCenter;
    $[44] = communicationPreferencesContextData.communicationTopics;
    $[45] = communicationPreferencesContextData.contactPermission.smsPermission;
    $[46] = customerProfileContextData;
    $[47] = mutate;
    $[48] = personId;
    $[49] = setCommunicationPreferencesContextData;
    $[50] = setCustomerProfileContextData;
    $[51] = topics;
    $[52] = t11;
  } else {
    t11 = $[52];
  }
  const handleSubmit = t11;
  useConfirmNavigation(isDirty, tabUrl);
  let t12;
  if ($[53] !== cmsContent.pushNotificationsMobileModalAcceptButtonTitle || $[54] !== cmsContent.pushNotificationsMobileModalCancelButtonTitle || $[55] !== cmsContent.pushNotificationsMobileModalDescription || $[56] !== cmsContent.pushNotificationsMobileModalTitle || $[57] !== cmsContent.pushNotificationsModalAcceptButtonTitle || $[58] !== cmsContent.pushNotificationsModalCancelButtonTitle || $[59] !== cmsContent.pushNotificationsModalDescription || $[60] !== cmsContent.pushNotificationsModalTitle) {
    t12 = {
      acceptButtonLabel: cmsContent.pushNotificationsModalAcceptButtonTitle,
      acceptButtonLabelMobile: cmsContent.pushNotificationsMobileModalAcceptButtonTitle,
      bodyText: cmsContent.pushNotificationsModalDescription,
      bodyTextMobile: cmsContent.pushNotificationsMobileModalDescription,
      cancelButtonLabel: cmsContent.pushNotificationsModalCancelButtonTitle,
      cancelButtonLabelMobile: cmsContent.pushNotificationsMobileModalCancelButtonTitle,
      headerTitle: cmsContent.pushNotificationsModalTitle,
      headerTitleMobile: cmsContent.pushNotificationsMobileModalTitle
    };
    $[53] = cmsContent.pushNotificationsMobileModalAcceptButtonTitle;
    $[54] = cmsContent.pushNotificationsMobileModalCancelButtonTitle;
    $[55] = cmsContent.pushNotificationsMobileModalDescription;
    $[56] = cmsContent.pushNotificationsMobileModalTitle;
    $[57] = cmsContent.pushNotificationsModalAcceptButtonTitle;
    $[58] = cmsContent.pushNotificationsModalCancelButtonTitle;
    $[59] = cmsContent.pushNotificationsModalDescription;
    $[60] = cmsContent.pushNotificationsModalTitle;
    $[61] = t12;
  } else {
    t12 = $[61];
  }
  const prepromptModalCmsContent = t12;
  let t13;
  if ($[62] !== cmsContent.languageLabel) {
    t13 = <h2 className={styles["site-preferences-form__heading"]}>{cmsContent.languageLabel}</h2>;
    $[62] = cmsContent.languageLabel;
    $[63] = t13;
  } else {
    t13 = $[63];
  }
  let t14;
  if ($[64] !== cmsContent.chooseLanguage) {
    t14 = <p className={styles["site-preferences-form__sub-heading"]}>{cmsContent.chooseLanguage}</p>;
    $[64] = cmsContent.chooseLanguage;
    $[65] = t14;
  } else {
    t14 = $[65];
  }
  let t15;
  if ($[66] !== cmsContent.requiredLabel) {
    t15 = {
      required: cmsContent.requiredLabel
    };
    $[66] = cmsContent.requiredLabel;
    $[67] = t15;
  } else {
    t15 = $[67];
  }
  let t16;
  if ($[68] !== cmsContent.preferredLanguageLabel || $[69] !== languagesOptions || $[70] !== t15) {
    t16 = <SelectField label={cmsContent.preferredLanguageLabel} name="preferredLanguage" options={languagesOptions} rules={t15} />;
    $[68] = cmsContent.preferredLanguageLabel;
    $[69] = languagesOptions;
    $[70] = t15;
    $[71] = t16;
  } else {
    t16 = $[71];
  }
  let t17;
  if ($[72] !== t13 || $[73] !== t14 || $[74] !== t16) {
    t17 = <div>{t13}{t14}{t16}</div>;
    $[72] = t13;
    $[73] = t14;
    $[74] = t16;
    $[75] = t17;
  } else {
    t17 = $[75];
  }
  let t18;
  if ($[76] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <hr className={styles["site-preferences-form__separator"]} />;
    $[76] = t18;
  } else {
    t18 = $[76];
  }
  let t19;
  if ($[77] !== interestsSectionCmsContent || $[78] !== topics) {
    t19 = <InterestsSection allTopics={topics} cmsContent={interestsSectionCmsContent} />;
    $[77] = interestsSectionCmsContent;
    $[78] = topics;
    $[79] = t19;
  } else {
    t19 = $[79];
  }
  let t20;
  if ($[80] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <hr className={styles["site-preferences-form__separator"]} />;
    $[80] = t20;
  } else {
    t20 = $[80];
  }
  let t21;
  if ($[81] !== cmsContent.emailNotificationLabel) {
    t21 = <h2 className={styles["site-preferences-form__heading"]}>{cmsContent.emailNotificationLabel}</h2>;
    $[81] = cmsContent.emailNotificationLabel;
    $[82] = t21;
  } else {
    t21 = $[82];
  }
  let t22;
  if ($[83] !== cmsContent.emailNotificationInformationLabel) {
    t22 = <p className={styles["site-preferences-form__content"]}>{cmsContent.emailNotificationInformationLabel}</p>;
    $[83] = cmsContent.emailNotificationInformationLabel;
    $[84] = t22;
  } else {
    t22 = $[84];
  }
  let t23;
  if ($[85] !== cmsContent.communicationTopicInformationLabel) {
    t23 = <legend className={styles["site-preferences-form__legend"]}>{cmsContent.communicationTopicInformationLabel}</legend>;
    $[85] = cmsContent.communicationTopicInformationLabel;
    $[86] = t23;
  } else {
    t23 = $[86];
  }
  let t24;
  if ($[87] !== emailNotifications) {
    t24 = emailNotifications.map(_temp10);
    $[87] = emailNotifications;
    $[88] = t24;
  } else {
    t24 = $[88];
  }
  let t25;
  if ($[89] !== t23 || $[90] !== t24) {
    t25 = <fieldset className={styles["site-preferences-form__fieldset"]}>{t23}{t24}</fieldset>;
    $[89] = t23;
    $[90] = t24;
    $[91] = t25;
  } else {
    t25 = $[91];
  }
  let t26;
  if ($[92] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = classNames(styles["site-preferences-form__content"], styles["site-preferences-form__content--reduce-spacing"]);
    $[92] = t26;
  } else {
    t26 = $[92];
  }
  let t27;
  if ($[93] !== cmsContent.communicationRemovalInformationLabel) {
    t27 = <p className={t26}>{cmsContent.communicationRemovalInformationLabel}</p>;
    $[93] = cmsContent.communicationRemovalInformationLabel;
    $[94] = t27;
  } else {
    t27 = $[94];
  }
  let t28;
  if ($[95] !== t22 || $[96] !== t25 || $[97] !== t27) {
    t28 = <div>{t22}{t25}{t27}</div>;
    $[95] = t22;
    $[96] = t25;
    $[97] = t27;
    $[98] = t28;
  } else {
    t28 = $[98];
  }
  let t29;
  if ($[99] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <hr className={styles["site-preferences-form__separator"]} />;
    $[99] = t29;
  } else {
    t29 = $[99];
  }
  let t30;
  if ($[100] !== cmsContent.SMSNotificationLabel) {
    t30 = <h2 className={styles["site-preferences-form__heading"]}>{cmsContent.SMSNotificationLabel}</h2>;
    $[100] = cmsContent.SMSNotificationLabel;
    $[101] = t30;
  } else {
    t30 = $[101];
  }
  let t31;
  if ($[102] !== cmsContent.SMSNotificationPermissionLabel) {
    t31 = <div className={styles["site-preferences-form__row"]}><CheckboxField label={cmsContent.SMSNotificationPermissionLabel} name="contactPermission.smsPermission" /></div>;
    $[102] = cmsContent.SMSNotificationPermissionLabel;
    $[103] = t31;
  } else {
    t31 = $[103];
  }
  let t32;
  if ($[104] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = <hr className={styles["site-preferences-form__separator"]} />;
    $[104] = t32;
  } else {
    t32 = $[104];
  }
  let t33;
  if ($[105] !== cmsContent.memberDirectoryTitle) {
    t33 = <h2 className={styles["site-preferences-form__heading"]}>{cmsContent.memberDirectoryTitle}</h2>;
    $[105] = cmsContent.memberDirectoryTitle;
    $[106] = t33;
  } else {
    t33 = $[106];
  }
  let t34;
  if ($[107] !== cmsContent.memberDirectoryLabel) {
    t34 = <CheckboxField label={cmsContent.memberDirectoryLabel} name="memberDirectoryOptIn" />;
    $[107] = cmsContent.memberDirectoryLabel;
    $[108] = t34;
  } else {
    t34 = $[108];
  }
  let t35;
  if ($[109] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = <hr className={styles["site-preferences-form__separator"]} />;
    $[109] = t35;
  } else {
    t35 = $[109];
  }
  let t36;
  if ($[110] !== cmsContent.pushNotificationsBlockedHint || $[111] !== cmsContent.pushNotificationsDescription || $[112] !== cmsContent.pushNotificationsTitle || $[113] !== prepromptModalCmsContent) {
    t36 = <PushNotificationPreferenceToggle description={cmsContent.pushNotificationsDescription} hintLabel={cmsContent.pushNotificationsBlockedHint} id="push-notifications" modalCmsContent={prepromptModalCmsContent} title={cmsContent.pushNotificationsTitle} />;
    $[110] = cmsContent.pushNotificationsBlockedHint;
    $[111] = cmsContent.pushNotificationsDescription;
    $[112] = cmsContent.pushNotificationsTitle;
    $[113] = prepromptModalCmsContent;
    $[114] = t36;
  } else {
    t36 = $[114];
  }
  const t37 = !isDirty || isMutationPending;
  let t38;
  if ($[115] !== cmsContent.saveChangesButtonLabel || $[116] !== t37) {
    t38 = <div className={styles["site-preferences-form__footer"]}><Button disabled={t37} type={ButtonType.SUBMIT}>{cmsContent.saveChangesButtonLabel}</Button></div>;
    $[115] = cmsContent.saveChangesButtonLabel;
    $[116] = t37;
    $[117] = t38;
  } else {
    t38 = $[117];
  }
  let t39;
  if ($[118] !== formMethods || $[119] !== handleSubmit || $[120] !== t17 || $[121] !== t19 || $[122] !== t21 || $[123] !== t28 || $[124] !== t30 || $[125] !== t31 || $[126] !== t33 || $[127] !== t34 || $[128] !== t36 || $[129] !== t38) {
    t39 = <FormWrapper className={styles["site-preferences-form"]} dataTestId="site-preferences-form" formMethods={formMethods} onSubmit={handleSubmit}>{t17}{t18}{t19}{t20}{t21}{t28}{t29}{t30}{t31}{t32}{t33}{t34}{t35}{t36}{t38}</FormWrapper>;
    $[118] = formMethods;
    $[119] = handleSubmit;
    $[120] = t17;
    $[121] = t19;
    $[122] = t21;
    $[123] = t28;
    $[124] = t30;
    $[125] = t31;
    $[126] = t33;
    $[127] = t34;
    $[128] = t36;
    $[129] = t38;
    $[130] = t39;
  } else {
    t39 = $[130];
  }
  return t39;
};
function _temp(item) {
  return item.name;
}
function _temp2(item_0) {
  return item_0.name;
}
function _temp3(array1, array2) {
  if (array1.length !== array2.length) {
    return true;
  }
  const names1 = array1.map(_temp).sort();
  const names2 = array2.map(_temp2).sort();
  return names1.some((name, index) => name !== names2[index]);
}
function _temp4(object1, object2) {
  if (!object1 || !object2) {
    return true;
  }
  const keys = new Set([...Object.keys(object1), ...Object.keys(object2)]);
  for (const key of keys) {
    if (object1[key] !== object2[key]) {
      return true;
    }
  }
  return false;
}
function _temp5(interest) {
  return interest.topicId;
}
function _temp6(interest_0) {
  return interest_0.topicId;
}
function _temp7(t0) {
  const [, value] = t0;
  return value;
}
function _temp8(t0) {
  const [key_0] = t0;
  return key_0;
}
function _temp9(item_1) {
  return item_1?.name;
}
function _temp10(emailNotification) {
  return <CheckboxField key={emailNotification.label} label={emailNotification.label} name={emailNotification.name} />;
}